/**
 * Signature Capture Component
 * Allows proctor to digitally sign drill reports
 */
import { Eraser, Check } from 'lucide-react'
import PropTypes from 'prop-types'
import React, { useRef, useState } from 'react'
import SignatureCanvas from 'react-signature-canvas'

import { Button } from '@/components/elements/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/elements/card'

export function SignatureCapture({ onSignatureComplete, required = true }) {
  const sigCanvas = useRef(null)
  const [isSigned, setIsSigned] = useState(false)

  const handleClear = () => {
    sigCanvas.current?.clear()
    setIsSigned(false)
    onSignatureComplete?.(null)
  }

  const handleEnd = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      setIsSigned(true)
      // Get base64 data URL
      const dataUrl = sigCanvas.current.toDataURL('image/png')
      onSignatureComplete?.(dataUrl)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Proctor Signature {required && <span className="text-destructive">*</span>}</span>
          {isSigned && (
            <span className="flex items-center gap-1 text-sm font-normal text-green-600">
              <Check className="h-4 w-4" />
              Signed
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="border-input rounded-lg border-[1.5px] bg-white">
          <SignatureCanvas
            ref={sigCanvas}
            canvasProps={{
              className: 'w-full h-48 touch-none',
            }}
            onEnd={handleEnd}
          />
        </div>

        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={handleClear} className="flex-1">
            <Eraser className="mr-2 h-4 w-4" />
            Clear
          </Button>
        </div>

        {required && !isSigned && (
          <p className="text-muted-foreground text-sm">
            Please sign above to complete the drill report
          </p>
        )}
      </CardContent>
    </Card>
  )
}

SignatureCapture.propTypes = {
  onSignatureComplete: PropTypes.func.isRequired,
  required: PropTypes.bool,
}
